import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search, Undo2 } from 'lucide-react';
import { fetchChecksQueue, fetchCheckVendors, undoCheck } from '../api/checks';
import type { Check, CheckStatus, QueueActionType, ReviewStatus, VendorOption } from '../api/checks';
import { detailText } from '../api/products';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/ExportButton';
import CheckDetailPanel from '../components/CheckDetailPanel';
import { HeaderActions } from '../components/HeaderActions';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
} from '../components/data-table/columns';
import {
  checkStatusColumn,
  nsIdColumn,
  pctDiffColumn,
  purchaseCostColumn,
  reviewerColumn,
  skuColumn,
  specialPriceColumn,
  timestampColumn,
  vendorColumn,
  vendorCostColumn,
} from '../components/data-table/checkColumns';
import { RowActionButton, RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  FilterBar,
  FilterField,
  FilterRow,
  ColumnsMenu,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDate, fmtVendor } from '../utils/format';
import { restoreRows, takeRows } from '../utils/optimistic';
import { useItemDetail } from '../hooks/useItemDetail';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const ACTION_TYPE_OPTIONS = [
  { value: '',                label: 'All' },
  { value: 'price_sync',      label: 'Price sync' },
  { value: 'auto_price_sync', label: 'Auto price sync' },
  { value: 'disable',         label: 'Disable' },
];

const STATUS_OPTIONS = [
  { value: '',       label: 'All' },
  { value: 'safe',   label: 'Safe',   dot: 'bg-green-500' },
  { value: 'unsafe', label: 'Unsafe', dot: 'bg-red-600'   },
];

/** What the push will do to this item once the sync job picks it up. */
function QueueActionBadge({ reviewStatus }: { reviewStatus: ReviewStatus }) {
  if (reviewStatus === 'disabled') return <Badge variant="danger" dot="bg-red-600">Disable</Badge>;
  if (reviewStatus === 'auto_approved') return <Badge variant="success" dot="bg-green-500">Auto price sync</Badge>;
  return <Badge variant="info" dot="bg-sky-500">Price sync</Badge>;
}

export default function PriceChecksQueuePage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [actionType, setActionType] = useState<'' | QueueActionType>('');
  const [status, setStatus]         = useState<'' | CheckStatus>('');
  const [queuedFrom, setQueuedFrom] = useState('');
  const [queuedTo, setQueuedTo]     = useState('');

  const [items, setItems]     = useState<Check[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const { detailFor, loadDetail } = useItemDetail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchChecksQueue({
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        action_type: actionType || undefined,
        status:      status || undefined,
        queued_from: queuedFrom || undefined,
        queued_to:   queuedTo || undefined,
        page,
        per_page: perPage,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, vendorCodes, actionType, status, queuedFrom, queuedTo]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchCheckVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  async function handleUndo(id: number) {
    const { remaining, snapshot } = takeRows(items, [id]);
    setActionError(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - 1);
    try {
      await undoCheck(id);
    } catch (e) {
      setItems(prev => restoreRows(prev, snapshot));
      setTotal(t => t + 1);
      const msg = e instanceof Error ? e.message : 'request failed';
      setActionError(`Undo failed — the row was restored. ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  function resetFilters() {
    setVendorCodes([]);
    setActionType('');
    setStatus('');
    setQueuedFrom('');
    setQueuedTo('');
    setPage(1);
  }

  const hasActiveFilters =
    vendorCodes.length > 0 || !!actionType || !!status || !!queuedFrom || !!queuedTo;

  const columns = useMemo<ColumnDef<Check>[]>(() => [
    expandColumn<Check>(),
    skuColumn,
    vendorColumn,
    nsIdColumn,
    purchaseCostColumn,
    vendorCostColumn,
    specialPriceColumn,
    pctDiffColumn,
    checkStatusColumn,
    {
      id: 'action',
      meta: { label: 'Action' },
      header: 'Action',
      cell: ({ row }) => <QueueActionBadge reviewStatus={row.original.review_status} />,
    },
    reviewerColumn,
    // Auto-approved rows queue at insert time (no reviewer click).
    timestampColumn('queuedAt', 'Queued at', c => c.reviewed_at ?? c.created_at),
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [
          {
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_item_id}?from=queue&checkId=${item.id}`,
          },
          { label: 'Open in NetSuite', icon: ExternalLink, href: `${NS_BASE_URL}${item.ns_item_id}` },
        ];
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento', icon: ExternalLink, href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot', icon: Camera, href: item.screenshot_url, separatorBefore: true,
          });
        }
        return (
          <RowActions>
            {/* Auto-synced rows are not undoable — the pause switch and the
                auto-approve setting are the brakes for the auto lane. */}
            {canReview && item.review_status !== 'auto_approved' && (
              <RowActionButton
                label="Return to Review"
                icon={Undo2}
                disabled={busy}
                onClick={() => void handleUndo(item.id)}
              />
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  useEffect(() => {
    if (expanded === true) return;
    Object.keys(expanded)
      .filter(id => expanded[id])
      .forEach(id => {
        const item = items.find(i => String(i.id) === id);
        if (item) void loadDetail(item.ns_item_id);
      });
  }, [expanded, items, loadDetail]);

  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="checks_queue"
          filters={{
            vendor_codes: vendorCodes.length ? vendorCodes : undefined,
            action_type: actionType || undefined,
            status: status || undefined,
            queued_from: queuedFrom || undefined,
            queued_to: queuedTo || undefined,
          }}
        />
      </HeaderActions>

      <PageHeading title="Price Checks — Queue" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setPage(1); }}
          />
          <FilterSelect
            label="Action"
            options={ACTION_TYPE_OPTIONS}
            value={actionType}
            onChange={v => { setActionType(v as typeof actionType); setPage(1); }}
          />
          <FilterSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={v => { setStatus(v as typeof status); setPage(1); }}
          />
          <FilterField label="Queued">
            <Input
              type="date"
              className="w-[140px]"
              value={queuedFrom}
              onChange={e => { setQueuedFrom(e.target.value); setPage(1); }}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={queuedTo}
              onChange={e => { setQueuedTo(e.target.value); setPage(1); }}
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="Queue is empty"
        renderExpanded={row => {
          const detail = detailFor(row.original.ns_item_id);
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    [
                      'Description',
                      detailText(detail.data, 'display_name') ?? detailText(detail.data, 'name') ?? '—',
                    ],
                    ['Manufacturer', detailText(detail.data, 'manufacturer') ?? '—'],
                    ['Type', detailText(detail.data, 'product_type') ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{row.original.sku ?? '—'}</span>],
                    ['Last checked', fmtDate(row.original.price_date ?? row.original.created_at)],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Price history">
                <CheckDetailPanel state={detail} currentId={row.original.id} />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={row.original.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}
